// import { getDeviceInfo } from "../utils/methods";
import { getDeviceInfo } from "../methods/getDeviceInfo";
import API from "./const";
import { GenericObject } from "../custom-types/generic-object";
import { User } from "../custom-types/user";
import { saveAuthToken } from "./utils/saveAuthToken";
import { getAuthHeader } from "./utils/getAuthHeader";
import { removeAuthToken } from "./utils/removeAuthToken";
import { toast } from "../components/toast/toast";
import { ga4sendEvent } from "./user-analytics.service";
import { validateAndConvertReqParamToInt } from "./utils/validateAndConvertReqParamToNum";
import { getNullApiResponse } from "./utils/get-null-api-response";
import { Cookies } from "react-cookie";
import { COOKIES } from "../constants/misc";
import { CompanyServiceType } from "../constants/serviceTypeId";

interface UserParams {
    phone_number: string | number;
    name?: string;
    otp?: number;
    lang?: number;
}

enum LoginMode {
    PASSWORD = 1,
    OTP = 2,
    SSO = 3
}

interface RegisterCompanyDetails {
    userId: string | number;
    name: string;
    serviceType: CompanyServiceType;
    placeId: string;
    referralCode?: string;
    sessionId: number;
    user_joining_bonus_enabled?: boolean;
}

async function registerCompanyDetails({
    userId,
    name,
    serviceType,
    placeId,
    referralCode,
    sessionId,
    user_joining_bonus_enabled
}: RegisterCompanyDetails) {
    try {
        const call = await fetch(
            new Request(API.REGISTER_COMPANY_DETAILS, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    ...getAuthHeader()
                },
                body: JSON.stringify({
                    user_id: parseInt(userId as string),
                    name,
                    service_type: serviceType,
                    place_id: placeId,
                    referral_code: referralCode,
                    session_id: sessionId,
                    user_joining_bonus_enabled: true
                })
            })
        );
        if (call.status === 200) {
            const response = await call.json();
            saveAuthToken(response.data.access_token);
            return response.data;
        }
    } catch (error) {
        toast.error("An error occured, please try again after sometime");
    }
    return null;
}

interface OtpLoginParams {
    phone_number: string | number;
    otp: number;
    is_wa_optin?: boolean;
}

interface OtpLoginErrorRes {
    message?: "Please register to continue" | "Please provide correct password";
    title?: "User does not exist" | "Password is incorrect";
    code?: "userNotFound" | "incorrectPassword";
}

interface OtpLoginRes extends OtpLoginErrorRes {
    data: User | null;
}

const ErrorMap: GenericObject = {
    "User does not exist": "userNotFound",
    "Password is incorrect": "incorrectPassword"
};

async function loginViaOtp({ phone_number, otp, is_wa_optin }: OtpLoginParams): Promise<OtpLoginRes> {
    try {
        const { browser, os, platform } = getDeviceInfo();
        const rqst: RequestInfo = new Request(API.LOGIN_VIA_OTP, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json"
                // key: process.env.REACT_APP_KEY || ""
            },
            body: JSON.stringify({
                phone_number: String(phone_number),
                otp: validateAndConvertReqParamToInt("otp", otp),
                mode: LoginMode.OTP,
                is_wa_optin: is_wa_optin || false,
                device_information: JSON.stringify({
                    model: platform.model,
                    brand: platform.vendor,
                    type: platform.type,
                    os_name: os.name,
                    os_version: os.version,
                    browser: browser.name,
                    browser_version: browser.version
                })
            })
        });

        const call = await fetch(rqst);
        if (call.status === 200) {
            const { data } = await call.json();
            saveAuthToken(data.access_token);
            return { data };
        }
        if (!call.ok) {
            const response = await call.json();
            toast.error(response.message);
            throw await call.json();
        }
    } catch (e) {
        // @ts-ignore aa
        return { data: null, code: ErrorMap[e.title] };
    }
    return { data: null };
}

async function sessionLogout(phone_number: number) {
    const validatedPhoneNumber = validateAndConvertReqParamToInt("phone_number", phone_number);
    const cookies = new Cookies();
    const token = cookies.get(COOKIES.token);

    if (!validatedPhoneNumber || !token) {
        return getNullApiResponse();
    }

    try {
        const rqst: RequestInfo = new Request(API.LOGOUT, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                ...getAuthHeader()
            },
            body: JSON.stringify({
                phone_number: String(validatedPhoneNumber)
            })
        });

        const call = await fetch(rqst);
        if (!call.ok) {
            const response = await call.json();
            toast.error(response.message);
            throw response;
        }
    } catch (err: unknown) {
        const error = err as { data?: unknown; message?: string };
        return { data: null, message: error.message };
    }
    return { data: null };
}

export const logout = (phoneNum: number) => {
    ga4sendEvent("Signup_logout_web", { value: 1 });
    sessionLogout(phoneNum).finally(removeAuthToken);
};

export const userAllDevicelogout = async (phoneNum: number) => {
    return sessionLogout(phoneNum).then(({ message }: any) => {
        !message && toast.success("User logged out successfully from all the devices");
    });
};

export const AuthService = {
    registerCompanyDetails,
    loginViaOtp,
    logout,
    userAllDevicelogout
};
